import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Box, Typography } from '@mui/material';

ChartJS.register(...registerables);

// Color palette for different machines
const machineColors = [
    'rgba(220, 89, 89, 0.7)',
    'rgba(91, 155, 213, 0.7)',
    'rgba(136, 198, 138, 0.7)',
    'rgba(91, 192, 222, 0.7)',
    'rgba(255, 179, 102, 0.7)',
    'rgba(193, 68, 255, 0.7)',
    'rgba(128, 128, 128, 0.7)',
    'rgba(255, 87, 178, 0.7)',
    'rgba(255, 206, 86, 0.7)',
    'rgba(75, 192, 192, 0.7)'
];

const CostVsUsageChart = ({ data = [] }) => {
    const scatterChartRef = useRef(null);
    const scatterChartInstance = useRef(null);

    // Default data if none provided
    const defaultData = [
        { machine_name: 'Table Saw', usage_hours: 400, cost: 30000 },
        { machine_name: '3D Printer', usage_hours: 500, cost: 50000 },
        { machine_name: '3D Scanner', usage_hours: 650, cost: 75000 },
        { machine_name: 'Laser Cutter', usage_hours: 680, cost: 120000 },
    ];

    const chartData = data.length > 0 ? data : defaultData;

    // Map data to machine format with colors
    const machineData = chartData.map((item, index) => ({
        x: item.usage_hours || item.hours || 0,
        y: item.cost || 0,
        name: item.machine_name || item.name || `Machine ${index + 1}`,
        color: machineColors[index % machineColors.length]
    }));

    const maxUsageHours = Math.max(...machineData.map(m => m.x), 100);
    const maxCost = Math.max(...machineData.map(m => m.y), 10000);

    useEffect(() => {
        if (scatterChartRef.current) {
            if (scatterChartInstance.current) {
                scatterChartInstance.current.destroy();
            }

            const ctx = scatterChartRef.current.getContext('2d');

            // Calculate axis ranges
            const xStepSize = Math.ceil(maxUsageHours / 5 / 100) * 100 || 100;
            const xMax = Math.ceil(maxUsageHours * 1.2 / xStepSize) * xStepSize;

            const yStepSize = Math.ceil(maxCost / 4 / 10000) * 10000 || 10000;
            const yMax = Math.ceil(maxCost * 1.2 / yStepSize) * yStepSize;

            scatterChartInstance.current = new ChartJS(ctx, {
                type: 'scatter',
                data: {
                    datasets: [{
                        label: 'Machines',
                        data: machineData.map(m => ({ x: m.x, y: m.y })),
                        backgroundColor: machineData.map(m => m.color),
                        borderColor: machineData.map(m => m.color.replace('0.7', '1')),
                        borderWidth: 1,
                        pointRadius: 10,
                        pointHoverRadius: 12
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'nearest',
                        intersect: true
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: { display: false },
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    const index = context.dataIndex;
                                    const machine = machineData[index];
                                    return `${machine.name}: ${context.parsed.x}h, ₹${(context.parsed.y / 1000).toFixed(1)}K`;
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            title: {
                                display: true,
                                text: 'Usage Hours',
                                color: '#666666',
                                font: { size: 12, family: 'Roboto' }
                            },
                            min: 0,
                            max: xMax,
                            ticks: {
                                stepSize: xStepSize,
                                color: '#666666',
                                font: { size: 10 }
                            },
                            grid: { color: '#f0f0f0' }
                        },
                        y: {
                            title: {
                                display: true,
                                text: 'Machine Cost (₹)',
                                color: '#666666',
                                font: { size: 12, family: 'Roboto' }
                            },
                            min: 0,
                            max: yMax,
                            ticks: {
                                stepSize: yStepSize,
                                color: '#666666',
                                font: { size: 10 },
                                callback: function (value) {
                                    return '₹' + (value / 1000) + 'K';
                                }
                            },
                            grid: { color: '#f0f0f0' }
                        }
                    },
                    onHover: (event, activeElements) => {
                        if (scatterChartInstance.current) {
                            scatterChartInstance.current.update('none');
                        }
                    }
                },
                plugins: [{
                    id: 'labelPlugin',
                    afterDraw: function (chart) {
                        const ctx = chart.ctx;
                        const activeElements = chart.getActiveElements();

                        if (activeElements.length > 0) {
                            const activeElement = activeElements[0];
                            const index = activeElement.index;
                            const machine = machineData[index];
                            const element = activeElement.element;
                            const x = element.x;
                            const y = element.y;

                            ctx.save();
                            ctx.fillStyle = '#333333';
                            ctx.font = 'bold 12px Roboto';
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'bottom';
                            ctx.fillText(machine.name, x, y - 18);
                            ctx.restore();
                        }
                    }
                }]
            });
        }
        return () => {
            if (scatterChartInstance.current) {
                scatterChartInstance.current.destroy();
            }
        };
    }, [data, machineData, maxUsageHours, maxCost]);

    const hasData = machineData.some(m => m.x > 0 || m.y > 0);

    return (
        <div style={{ flex: 1, height: '100%', position: 'relative', width: '100%' }}>
            {!hasData && data.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    <Typography sx={{ color: '#999', fontSize: '14px' }}>
                        No cost vs usage data available
                    </Typography>
                </Box>
            ) : (
                <canvas ref={scatterChartRef} style={{ width: '100%', height: '100%' }}></canvas>
            )}
        </div>
    );
};

export default CostVsUsageChart;
